import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { FlaskConical, BarChart3, Search, Shield, TrendingUp, Menu, X, User } from 'lucide-react';
import { ScoreInfoDialog, AboutSystemDialog } from '@/components/ScoreInfoDialogs';
import { Footer } from '@/components/Footer';
import GlobalAuthorMap from '@/components/GlobalAuthorMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { ThemeToggle } from '@/components/ThemeToggle';

const HomePage = () => {
  const [journalMetrics, setJournalMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthorLoggedIn, setIsAuthorLoggedIn] = useState(false);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(`${API_URL}/api/journal-metrics`);
        const data = await response.json();
        if (data.success) {
          setJournalMetrics(data.metrics);
        }
      } catch (error) {
        void 0;
      } finally {
        setLoading(false);
      }
    };

    // Check if author is logged in
    const authorAuth = sessionStorage.getItem('author_authenticated');
    setIsAuthorLoggedIn(!!authorAuth);

    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageSEO 
        title="Evidentica - AI-Powered Medical Research Intelligence Platform"
        description="Discover and analyze medical research with advanced AI insights. Access evidence-based studies, impact analysis, and comprehensive article evaluations from leading journals."
        keywords="medical research, scientific articles, evidence-based medicine, AI research analysis, clinical studies, research intelligence, impact factor, systematic review"
        canonical={process.env.REACT_APP_BACKEND_URL || ''}
      />
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-[#1a1a2e] dark:to-[#16213e]">
      {/* Navigation */}
      <nav className="bg-white dark:bg-[#1a1a2e] border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center space-x-2">
              <FlaskConical className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <span className="text-xl font-bold text-slate-900 dark:text-slate-100">Evidentica</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-4">
              <Link to="/about">
                <Button variant="ghost">About</Button>
              </Link>
              <Link to="/explorer">
                <Button variant="ghost" data-testid="nav-explorer-btn">Explorer</Button>
              </Link>
              {isAuthorLoggedIn ? (
                <Link to="/author/dashboard">
                  <Button variant="ghost" data-testid="nav-author-dashboard-btn">
                    <User className="mr-2 h-4 w-4" />
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/author/login">
                  <Button variant="ghost" data-testid="nav-author-login-btn">Login</Button>
                </Link>
              )}
              <ThemeToggle />
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-1">
              <ThemeToggle />
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-slate-900 dark:text-slate-100" />
                ) : (
                  <Menu className="h-6 w-6 text-slate-900 dark:text-slate-100" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2">
              <Link to="/about" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start">About</Button>
              </Link>
              <Link to="/explorer" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start" data-testid="nav-explorer-btn">Explorer</Button>
              </Link>
              {isAuthorLoggedIn ? (
                <Link to="/author/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start" data-testid="nav-author-dashboard-btn">
                    <User className="mr-2 h-4 w-4" />
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/author/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start" data-testid="nav-author-login-btn">Login</Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-4 sm:mb-6" data-testid="hero-title">
            Evidentica
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-slate-600 dark:text-slate-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-2">
            Research Visibility Platform for Journal Articles
            Analyze study designs, calculate evidence levels, and explore medical literature with confidence.
          </p>
          <div className="flex justify-center">
            <Link to="/explorer">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="explore-btn">
                <Search className="mr-2 h-5 w-5" />
                Explore Research
              </Button>
            </Link>
          </div>
        </div>

        {/* Journal Metrics Section */}
        <div className="mt-20">
          <div className="text-center mb-10 flex flex-col items-center justify-center">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3 flex items-center justify-center">
              <TrendingUp className="mr-3 h-8 w-8 text-blue-600 dark:text-blue-400" />
              Journal Performance Metrics
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Average Methodology Impact Index by Journal
            </p>
          </div>

          {loading ? (
            <div className="text-center text-slate-500">Loading metrics...</div>
          ) : (
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {journalMetrics.map((metric, index) => (
                <Card 
                  key={index} 
                  className={`border-2 transition-all hover:shadow-lg w-full sm:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] ${
                    index === 0 ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30' :
                    index === 1 ? 'border-gray-400 bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/30 dark:to-slate-900/30' :
                    index === 2 ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30' :
                    'border-blue-200 bg-blue-50 dark:bg-blue-950/30'
                  }`}
                >
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300 min-h-[48px]">
                      {metric.journal}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className={`text-4xl sm:text-5xl font-bold mb-2 ${
                        index === 0 ? 'text-yellow-600' :
                        index === 1 ? 'text-gray-600' :
                        index === 2 ? 'text-orange-600' :
                        'text-blue-600'
                      }`}>
                        {metric.avg_impact_index}
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">Average Impact Index</p>
                      <div className="pt-3 border-t border-slate-300 dark:border-slate-600">
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          <span className="font-semibold">{metric.article_count}</span> {metric.article_count === 1 ? 'article' : 'articles'} analyzed
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!loading && journalMetrics.length === 0 && (
            <div className="text-center text-slate-500">
              No journal metrics available yet.
            </div>
          )}
        </div>

        {/* Global Author Map */}
        <div className="mt-20">
          <GlobalAuthorMap />
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-16 sm:mt-20">
          <Card data-testid="feature-impact-card">
            <CardHeader>
              <BarChart3 className="h-10 w-10 text-blue-600 mb-2" />
              <CardTitle>Impact Index (0-100)</CardTitle>
              <CardDescription>
                Comprehensive scoring combining AI Methodology analysis (max 70) with Data Integrity multiplier, and research visibility metrics (max 30)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                <li>&#8226; AI-powered methodology evaluation</li>
                <li>&#8226; Data Integrity Check as score multiplier</li>
                <li>&#8226; Clinical Learning Value for Case Reports (0-35)</li>
                <li>&#8226; Visibility from citations, downloads & social impact</li>
              </ul>
            </CardContent>
          </Card>

          <Card data-testid="feature-ai-analysis-card" className="border-purple-200 bg-purple-50 dark:bg-purple-950/20 dark:border-purple-800">
            <CardHeader>
              <FlaskConical className="h-10 w-10 text-purple-600 mb-2" />
              <CardTitle>AI Research Analysis</CardTitle>
              <CardDescription>
                Comprehensive AI-powered analysis with structured scientific knowledge extraction and Data Integrity assessment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                <li>&#8226; Core scientific idea extraction</li>
                <li>&#8226; PICO framework analysis</li>
                <li>&#8226; Data Integrity verification</li>
                <li>&#8226; Clinical Learning Value for Case Reports</li>
                <li>&#8226; Research gaps & future directions</li>
              </ul>
            </CardContent>
          </Card>

          <Card data-testid="feature-explorer-card">
            <CardHeader>
              <Search className="h-10 w-10 text-purple-600 mb-2" />
              <CardTitle>Public Explorer</CardTitle>
              <CardDescription>
                Discover and compare research articles ranked by impact, with PDF downloads, view tracking, and author profiles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                <li>&#8226; Filter by study type & evidence level</li>
                <li>&#8226; PDF download with tracking</li>
                <li>&#8226; Author profiles & global map</li>
                <li>&#8226; Visibility score visualization</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Disclaimer */}
        <div className="mt-16 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-300 mb-2">Important Notice</h3>
          <p className="text-amber-800 dark:text-amber-400">
            The Impact Index is a methodological indicator designed to assess study design strength and research visibility.
            It does not represent clinical truth, guideline recommendations, or treatment standards.
          </p>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
};

export default HomePage;
